/**
 * Plan 04-02 (DRC-05) — Magic-link set-password page.
 *
 * Mounted at /set-password as a PUBLIC route (outside ProtectedRoute) in
 * AppRouter.tsx so unauthenticated users hitting the email link can access it.
 *
 * Flow: read ?token= from query → user enters + confirms password → POST to
 * /api/v1/auth/set-password via apiPublic (B2 lock — no Authorization header)
 * → on 200 receive { access_token } → call AuthContext.bootstrapFromToken
 * (W4 lock option-a) → navigate to /dashboard.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import FormControl from '../components/common/FormControl';
import Button from '../components/common/Button';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { extractApiError } from '../utils/apiError';

const SetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  // W4 lock option-a: AuthContext exposes a typed bootstrapFromToken helper.
  const { bootstrapFromToken } = useAuth();

  if (!token) {
    return (
      <div className="auth-page-shell" style={shellStyle}>
        <div className="auth-card" style={cardStyle}>
          <h2>Invalid link</h2>
          <p>No token provided. Please use the link from your onboarding email.</p>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const tok = await authService.setPassword({ token, new_password: pw });
      const ok = await bootstrapFromToken(tok.access_token);
      if (!ok) {
        // Bootstrap failed (e.g. /me returned 401); send the user to /login.
        navigate('/login', { replace: true });
        return;
      }
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      setErr(extractApiError(e, 'Failed to set password.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page-shell" style={shellStyle}>
      <form className="auth-card" style={cardStyle} onSubmit={handleSubmit}>
        <h2 style={{ marginTop: 0 }}>Set Your Password</h2>
        <p className="auth-card-sub" style={{ color: '#6b7280', marginTop: -8 }}>
          Pick a password to finish setting up your account.
        </p>
        {err && (
          <div
            className="auth-error-banner"
            role="alert"
            style={{
              padding: '8px 12px',
              border: '1px solid #fecaca',
              background: '#fef2f2',
              color: '#991b1b',
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {err}
          </div>
        )}
        <FormControl.Input
          data-testid="INP_set_password_new"
          type="password"
          label="New password"
          value={pw}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw(e.target.value)}
          required
          minLength={8}
          autoFocus
        />
        <FormControl.Input
          data-testid="INP_set_password_confirm"
          type="password"
          label="Confirm password"
          value={pw2}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw2(e.target.value)}
          required
          minLength={8}
        />
        <Button data-testid="BTN_set_password_submit" variant="fill" type="submit" disabled={busy}>
          {busy ? 'Setting…' : 'Set password and log in'}
        </Button>
      </form>
    </div>
  );
};

// Inline styles fallback — keeps the page presentable even if the
// `auth-page-shell` / `auth-card` classnames aren't defined yet in global.css.
const shellStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f9fafb',
  padding: 16,
};

const cardStyle: React.CSSProperties = {
  maxWidth: 420,
  width: '100%',
  background: '#fff',
  padding: 32,
  borderRadius: 12,
  boxShadow: '0 4px 24px rgba(15,23,42,0.08)',
};

export default SetPasswordPage;
